import { useRoute, useRouter } from 'vue-router'
import { RANGES, BOOKED_BY, PAGE_TYPES } from 'stores/analytics.js'

// Mirrors the Bookings and Traffic dashboard state into the query string, so
// what you are looking at is a link you can send to someone else. Same contract
// as useTaskStatsUrlState: URL beats localStorage on load, hydrating from a URL
// never writes to localStorage, and defaults are omitted so an untouched
// dashboard stays a clean `/analytics/bookings`.
//
// Lists are comma separated rather than repeated `event[]=` pairs. These get
// pasted into chat, and `?status=confirmed,canceled` survives that trip far
// better than the percent-encoded bracket form. The API call built by the store
// uses the bracket form because that is what Rails parses; the two shapes are
// deliberately different and only meet in the store.

const VALID_RANGES = RANGES.map(r => r.value)

// Which query keys belong to which dashboard, and how each maps onto the store's
// filter object. One table so a new filter is added in exactly one place.
const SCHEMA = {
  bookings: [
    { key: 'event',     field: 'eventIds',  list: true },
    { key: 'status',    field: 'statuses',  list: true },
    { key: 'location',  field: 'locations', list: true },
    { key: 'booked_by', field: 'bookedBy',  list: false, allowed: BOOKED_BY },
  ],
  traffic: [
    { key: 'channel', field: 'channels',  list: true },
    { key: 'country', field: 'countries', list: true },
    { key: 'device',  field: 'devices',   list: true },
    { key: 'page',    field: 'pageType',  list: false, allowed: PAGE_TYPES },
  ],
}

function splitList(value) {
  if (typeof value !== 'string' || !value) return null
  const parts = value.split(',').map(v => v.trim()).filter(Boolean)
  return parts.length ? parts : null
}

export function useAnalyticsUrlState() {
  const route = useRoute()
  const router = useRouter()

  // Null for anything the URL did not mention, which is what lets the caller
  // fall back to localStorage rather than stomping a remembered value.
  function readState(tab) {
    const q = route.query
    const filters = {}

    ;(SCHEMA[tab] ?? []).forEach(({ key, field, list, allowed }) => {
      if (list) {
        const parsed = splitList(q[key])
        if (parsed) filters[field] = parsed
      } else if (allowed.includes(q[key])) {
        filters[field] = q[key]
      }
    })

    return {
      range: VALID_RANGES.includes(q.range) ? q.range : null,
      filters: Object.keys(filters).length ? filters : null,
    }
  }

  function buildQuery(tab, range, filters) {
    const q = {}
    // The range is always written, unlike the task dashboard's. It is the one
    // control someone sending a link almost always means to include, and it
    // otherwise reads back as the recipient's own remembered range.
    if (range) q.range = range

    ;(SCHEMA[tab] ?? []).forEach(({ key, field, list }) => {
      const value = filters?.[field]
      if (list) {
        if (value?.length) q[key] = value.join(',')
      } else if (value) {
        q[key] = value
      }
    })
    return q
  }

  function sameAsCurrent(query) {
    const current = route.query
    const keys = new Set([...Object.keys(query), ...Object.keys(current)])
    return [...keys].every(k => String(query[k] ?? '') === String(current[k] ?? ''))
  }

  // Always `replace`. Unlike a tab, a filter is an adjustment to where you
  // already are, and pushing each one would turn Back into an undo trail of
  // every checkbox touched. Moving between sections is a router-link and
  // pushes on its own.
  function writeState(tab, range, filters) {
    const query = buildQuery(tab, range, filters)
    // Vue Router treats navigating to an identical location as a failure, so
    // this guard is load bearing rather than an optimisation.
    if (sameAsCurrent(query)) return

    router.replace({ path: route.path, query }).catch(() => {
      // A navigation cancelled by a newer one is normal here.
    })
  }

  return { readState, writeState }
}
